<template>
  <article id="atmospheric-correction">
    <h1>大气校正</h1>

    <section id="atm-correction-intro">
      <h2>一、简介</h2>
      <p>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;大气校正是遥感数据预处理中的关键步骤，目的是消除大气层对遥感影像辐亮度的干扰，将表观辐亮度转换为具有物理意义的地表反射率（Surface Reflectance）。该模块基于先进的辐射传输模型，结合影像元数据与大气信息库，实现高精度、高自动化的大气校正计算。
      </p>
      <p>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;本模块具备以下核心优势：
      </p>
      <ul>
        <li>全自动提取太阳角度、观测角度、高程、大气类型等参数；</li>
        <li>动态调用辐射传输模型，计算传输路径、散射项、吸收项；</li>
        <li>输出真实地表反射率产品，为后续分类、反演、变化检测等提供数据保障。</li>
      </ul>
    </section>

    <section id="atm-correction-interface">
      <h2>二、功能界面</h2>
      <div class="image-section">
        <div class="image-container">
          <img src="@/assets/documentation/daqi.png" alt="大气校正界面" class="software-image" />
          <p class="image-caption">图5：大气校正界面 - 基于自研辐射传输模型消除大气干扰，计算地表反射率</p>
        </div>
      </div>
      <p>界面包含以下功能区域：</p>
      <ul>
        <li>输入影像选择区（支持单景或批量）</li>
        <li>自动识别卫星/观测信息预览</li>
        <li>大气参数选项（自动/手动）</li>
        <li>地形高程参数设置</li>
        <li>输出路径设定区</li>
        <li>"开始校正"按钮 + 实时进度条</li>
      </ul>
      <p class="note">🌫️ 大气校正是遥感数据处理的关键步骤，通过先进的辐射传输模型消除大气层对影像的影响，获得真实的地表反射率信息。</p>
    </section>

    <section id="atm-correction-params">
      <h2>三、用户参数</h2>
      <table>
        <thead>
          <tr>
            <th>参数名称</th>
            <th>功能说明</th>
            <th>获取方式</th>
            <th>示例值</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>输入影像路径</td>
            <td>已定标的辐亮度影像（来自上一步）</td>
            <td>用户选择</td>
            <td>D:\data\GF1_L1_rad.tif</td>
          </tr>
          <tr>
            <td>传感器信息</td>
            <td>卫星平台、传感器类型等元数据</td>
            <td>自动识别</td>
            <td>GF-1_WFV</td>
          </tr>
          <tr>
            <td>太阳高度/方位角</td>
            <td>太阳观测角度信息</td>
            <td>自动读取或手动输入</td>
            <td>高度：45.6°；方位角：125.3°</td>
          </tr>
          <tr>
            <td>观测视角</td>
            <td>卫星入射方向信息</td>
            <td>自动读取</td>
            <td>视天顶角：4.2°；方位角：210°</td>
          </tr>
          <tr>
            <td>地表高程</td>
            <td>平均地形高程，用于修正大气路径长度</td>
            <td>自动提取或手动输入</td>
            <td>450 m</td>
          </tr>
          <tr>
            <td>大气类型</td>
            <td>大气模型（热带、中纬度夏、冬等）</td>
            <td>默认自动匹配</td>
            <td>中纬度夏季</td>
          </tr>
          <tr>
            <td>气溶胶模型</td>
            <td>模拟气溶胶类型（城市、农村、海洋等）</td>
            <td>默认自动匹配</td>
            <td>大陆型气溶胶</td>
          </tr>
          <tr>
            <td>输出路径</td>
            <td>地表反射率输出目录</td>
            <td>手动设置</td>
            <td>D:\result\reflectance\</td>
          </tr>
        </tbody>
      </table>
      <p class="note">🔍 提示：所有参数默认自动配置，专业用户可开启"高级参数"选项进行自定义设置。</p>
    </section>

    <section id="atm-correction-steps">
      <h2>四、操作步骤</h2>
      <ol>
        <li>
          <strong>导入辐亮度影像</strong>
          <p>选择上一步输出的 .tif 格式辐亮度影像。</p>
        </li>
        <li>
          <strong>自动提取参数</strong>
          <ul>
            <li>软件自动解析影像元数据（卫星、时间、角度）；</li>
            <li>并结合 DEM 数据提取影像区域平均高程。</li>
          </ul>
        </li>
        <li>
          <strong>辐射传输计算</strong>
          <ul>
            <li>构建大气剖面（压力、温度、水汽、臭氧）；</li>
            <li>计算每个波段的路径辐射项、散射项和传输函数。</li>
          </ul>
        </li>
        <li>
          <strong>反射率转换</strong>
          <ul>
            <li>根据辐射传输模型，将辐亮度影像转换为地表反射率影像；</li>
            <li>支持每个像素逐波段运算，精度优于 LUT 查表法。</li>
          </ul>
        </li>
        <li>
          <strong>结果保存</strong>
          <ul>
            <li>自动命名输出文件，附加 _ref 后缀；</li>
            <li>保存完整日志与参数 JSON 文件以供追溯。</li>
          </ul>
        </li>
      </ol>
    </section>

    <section id="atm-correction-results">
      <h2>五、用户结果</h2>
      <table>
        <thead>
          <tr>
            <th>输出文件名</th>
            <th>类型</th>
            <th>内容说明</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>GF1_WFV_20230510_L2_ref.tif</td>
            <td>GeoTIFF影像</td>
            <td>地表反射率产品，单位：无量纲</td>
          </tr>
          <tr>
            <td>GF1_WFV_20230510_L2_ref.log</td>
            <td>日志文件</td>
            <td>包含模型参数、处理信息、运行时间等</td>
          </tr>
          <tr>
            <td>meta_GF1_WFV_ref.json</td>
            <td>参数文件</td>
            <td>大气模型、太阳角度、输入信息记录等</td>
          </tr>
        </tbody>
      </table>
      <p class="note">✅ 反射率产品可直接用于植被指数计算、分类、变化检测、能量平衡建模等高阶应用。</p>
      <p class="note">✅ 所有结果均具备空间参考信息，兼容 ENVI、ArcGIS、QGIS 等软件平台。</p>
    </section>
  </article>
</template>

<script>
export default {
  name: 'AtmosphericCorrection',
  props: {
    targetSectionId: {
      type: String,
      default: null,
    },
  },
  watch: {
    targetSectionId(newId) {
      if (newId) {
        this.$emit('scroll-to-section', newId);
      }
    },
  },
  mounted() {
    if (this.targetSectionId) {
      this.$emit('scroll-to-section', this.targetSectionId);
    }
  },
};
</script>

<style scoped>
/* 图片展示样式 */
.image-section {
  margin: 30px 0;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.image-container {
  text-align: center;
  background: rgba(var(--card-bg), 0.8);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(var(--primary), 0.1);
  border: 1px solid rgba(var(--primary), 0.1);
}

.software-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.software-image:hover {
  transform: scale(1.02);
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.3);
}

.image-caption {
  margin-top: 15px;
  font-size: 14px;
  color: var(--text-secondary);
  font-style: italic;
  line-height: 1.5;
}

/* 添加计数器重置 */
article {
  counter-reset: list-item;
}

/* 重用 Document.vue 中的表格和列表样式 */
table {
  width: 100%;
  border-collapse: collapse;
  margin: 25px 0;
  font-size: 15px;
  text-align: left;
  box-shadow: 0 0 15px rgba(var(--primary), 0.1);
  border-radius: 8px;
  overflow: hidden;
}

th,
td {
  padding: 12px 15px;
  border: 1px solid rgba(var(--primary), 0.2);
  color: var(--text-primary);
}

thead tr {
  background-color: rgba(var(--primary), 0.2);
  color: var(--primary);
  text-shadow: 0 0 5px rgba(var(--primary), 0.5);
  font-weight: bold;
}

tbody tr {
  background-color: rgba(var(--card-bg), 0.6);
}

tbody tr:nth-of-type(even) {
  background-color: rgba(var(--card-bg), 0.8);
}

tbody tr:hover {
  background-color: rgba(var(--primary), 0.1);
  color: var(--primary);
  text-shadow: 0 0 3px rgba(var(--primary), 0.3);
}

/* List Styles (Ordered and Unordered) */
ol,
ul {
  list-style: none;
  padding-left: 20px;
  margin: 20px 0;
}

ol li,
ul li {
  margin-bottom: 10px;
  color: var(--text-primary);
  line-height: 1.6;
  position: relative;
}

ol li {
  counter-increment: list-item;
}

ol li strong,
ul li strong {
  color: var(--primary);
  text-shadow: 0 0 5px rgba(var(--primary), 0.4);
}

ol li::before {
  content: counter(list-item) ". ";
  color: var(--secondary);
  font-weight: bold;
  position: absolute;
  left: -25px;
  top: 0;
  width: 20px;
  text-align: right;
  text-shadow: 0 0 5px rgba(var(--secondary), 0.5);
}

ul li::before {
  content: "\2705";
  color: var(--secondary);
  font-size: 1.1em;
  position: absolute;
  left: -50px;
  top: 0;
  text-shadow: 0 0 5px rgba(var(--secondary), 0.5);
}

ul li p {
  margin-left: 0;
  margin-bottom: 5px;
}

p.note {
  background-color: rgba(var(--secondary), 0.1);
  border-left: 5px solid var(--secondary);
  padding: 10px 15px;
  margin-top: 20px;
  border-radius: 4px;
  color: var(--text-primary);
  font-size: 14px;
}
</style> 